import programs from '@app/programs'
import { useAppSelector } from '@app/store/hooks'
import { useMemo } from 'react'
import { useParams, useLocation } from 'react-router-dom'
import useStaticContent from './useStaticContent'

/**
 * Hook that returns campaign data from local configuration files with persistent message loading during language changes
 * @param enabled - Boolean determining whether to execute the hook
 * @returns The localised messages and status of the campaign
 */
const useStaticCampaign = (enabled: boolean) => {
  const location = useLocation()
  const { program } = useParams<{ program: string }>()
  const { language } = useAppSelector(state => state.settings)
  const { content: staticContent, loaded: staticContentLoaded, isInitialLoad } = useStaticContent()

  /** Array of path parts which fit the `:program` URL param but behave as campaigns  */
  const campaignWhitelist = useMemo(() => {
    if (!enabled) {
      return []
    }

    return Object.keys(programs)
  }, [enabled])

  /** The static data for the current program */
  const campaignData = useMemo(() => {
    if (!enabled || !program) {
      return undefined
    }

    return programs[program]
  }, [enabled, program])

  /** The translated copy for the current campaign */
  const messages = useMemo(() => {
    if (!enabled || !staticContentLoaded) {
      return {}
    }

    // Return the static content if no program is found so non-campaign pages display correctly
    if (!program) {
      return staticContent
    }

    // Return a specific language object when a program is found and inject campaign assets into the static content
    if (campaignWhitelist.includes(program)) {
      return {
        ...staticContent,
        ...programs[program].languagePack[language],
        // Map campaign specific assets to the correct intl content
        'landing_page.content.banner.image': programs[program].bannerImg,
        'closed_page.content.banner.image': programs[program].bannerImg,
        'closed_page.content.banner.button_href': programs[program].partner_url,
      }
    }

    // Return the default language object if the program is not found
    return { ...staticContent, ...programs.fr.languagePack.en }
  }, [program, language, campaignWhitelist, staticContent, staticContentLoaded, enabled])

  const campaignNotFound = useMemo(() => {
    if (!enabled) {
      return false
    }

    const requestedProgramExists = program && program in programs

    if (requestedProgramExists) {
      return false
    }

    return true
  }, [enabled, program])

  const campaignEnded = useMemo(() => {
    if (!enabled) {
      return false
    }

    /** The URLs not allowed when the campaign has ended */
    const blacklistUrls = /(at|de|fr|it|unieuro)(\/quiz\/(customer|model|purchase|valuation|shipping|payment))?/

    /** The URLs allowed when the campaign has ended */
    const whitelistUrls = /(at|de|fr|it|unieuro)\/(faqs|terms|privacy|cookies)/

    if (
      !location.pathname.includes('closed') &&
      program &&
      campaignData?.closed &&
      blacklistUrls.test(location.pathname) &&
      !whitelistUrls.test(location.pathname)
    ) {
      return true
    }

    return false
  }, [enabled, location, program, campaignData])

  // Do not execute the hook if it is not enabled
  if (!enabled) {
    return {
      messages: {},
      messagesLoaded: false,
      notFound: false,
      ended: false,
      isInitialLoad: true,
    }
  }

  return {
    messages,
    messagesLoaded: staticContentLoaded && Object.keys(messages).length > 0,
    notFound: campaignNotFound,
    ended: campaignEnded,
    isInitialLoad,
  }
}

export default useStaticCampaign
